import { useState } from "react";
import { Draggable } from "@hello-pangea/dnd";
import {
  ActionIcon,
  Badge,
  Box,
  Card,
  Group,
  Switch,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconAward,
  IconBolt,
  IconCalendarEvent,
  IconChevronDown,
  IconChevronUp,
  IconClockHour4,
  IconGripVertical,
  IconLayoutList,
  IconMicrophone,
  IconSpeakerphone,
} from "@tabler/icons-react";
import { HOME_SCREEN_SECTIONS_METADATA } from "@congrs/schemas";

// Each section's mark in the list, so a row is recognizable before it is read.
// Presentation only, which is why it lives here rather than in the shared
// metadata the app reads.
const SECTION_ICONS = {
  nowNext: { Icon: IconClockHour4, color: "indigo" },
  quickActions: { Icon: IconBolt, color: "yellow" },
  announcements: { Icon: IconSpeakerphone, color: "orange" },
  agenda: { Icon: IconCalendarEvent, color: "blue" },
  speakers: { Icon: IconMicrophone, color: "violet" },
  sponsors: { Icon: IconAward, color: "teal" },
};

// A section added to the shared metadata without a mark here still draws a row.
const DEFAULT_ICON = { Icon: IconLayoutList, color: "gray" };

/**
 * One home screen section in the settings list: drag handle, label and
 * description on a single line, then the visibility toggle.
 *
 * `disabled` means the section's feature is off for this event — the organizer
 * can't switch it on from here, so the row explains itself and stays put.
 *
 * A section with per-section settings passes them as `settings`, which the row
 * reveals under a chevron — switched off too, so the organizer can set a
 * section up before turning it on. `description` overrides the static one from
 * the metadata, so a configured section can summarize its own settings.
 */
export default function HomeScreenSectionCard({
  sectionKey,
  index,
  visible,
  disabled,
  onToggle,
  description,
  settings,
}) {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((current) => !current);
  const metadata = HOME_SCREEN_SECTIONS_METADATA[sectionKey];
  const { Icon, color } = SECTION_ICONS[sectionKey] ?? DEFAULT_ICON;
  const canDrag = !disabled;
  const canExpand = Boolean(settings);

  return (
    <Draggable draggableId={sectionKey} index={index} isDragDisabled={!canDrag}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          withBorder
          shadow={snapshot.isDragging ? "lg" : "sm"}
          px="md"
          py="sm"
          style={{
            ...provided.draggableProps.style,
            backgroundColor: snapshot.isDragging
              ? "var(--mantine-color-blue-0)"
              : undefined,
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              <Box
                {...provided.dragHandleProps}
                style={{
                  display: "flex",
                  cursor: canDrag ? "grab" : "default",
                  opacity: canDrag ? 1 : 0.3,
                }}
              >
                <IconGripVertical size={18} />
              </Box>
              <ThemeIcon variant="light" color={color} radius="md" size="lg">
                <Icon size={20} />
              </ThemeIcon>
              <Box
                onClick={canExpand ? toggleOpen : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--mantine-spacing-md)",
                  flex: 1,
                  minWidth: 0,
                  cursor: canExpand ? "pointer" : "default",
                }}
              >
                <Text fw={600} w={150} style={{ flexShrink: 0 }}>
                  {metadata.label}
                </Text>
                <Text c="dimmed" size="sm" truncate>
                  {description ?? metadata.description}
                </Text>
              </Box>
            </Group>

            <Group gap="md" wrap="nowrap">
              {disabled && (
                <Badge
                  color="gray"
                  variant="outline"
                  style={{ textTransform: "none" }}
                >
                  Feature turned off for this event
                </Badge>
              )}
              <Switch
                checked={visible}
                disabled={disabled}
                onChange={onToggle}
              />
              {canExpand && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={toggleOpen}
                  aria-label={open ? "Hide settings" : "Show settings"}
                >
                  {open ? (
                    <IconChevronUp size={18} />
                  ) : (
                    <IconChevronDown size={18} />
                  )}
                </ActionIcon>
              )}
            </Group>
          </Group>

          {open && settings}
        </Card>
      )}
    </Draggable>
  );
}
